/**
 * ProceduralSprites.ts - Generate placeholder sprites at runtime
 * Creates FF1-style placeholder graphics when sprite sheets aren't available
 * Can be used as fallback or for procedural content generation
 */

import { TerrainType, TERRAIN_COLORS } from './data/TilesetConfig';

export class ProceduralSprites {
  private tilesetCanvas: HTMLCanvasElement | null = null;
  private characterCanvas: HTMLCanvasElement | null = null;
  private enemyCanvas: HTMLCanvasElement | null = null;
  private effectsCanvas: HTMLCanvasElement | null = null;

  /**
   * Generate all placeholder spritesheets
   */
  generateAll(): {
    worldTileset: HTMLCanvasElement;
    characters: HTMLCanvasElement;
    enemies: HTMLCanvasElement;
    effects: HTMLCanvasElement;
  } {
    return {
      worldTileset: this.generateWorldTileset(),
      characters: this.generateCharacters(),
      enemies: this.generateEnemies(),
      effects: this.generateEffects(),
    };
  }

  /**
   * Generate world tileset (256x256, 16x16 tiles)
   * Now with enhanced FF1-style graphics matching the new terrain types
   */
  generateWorldTileset(): HTMLCanvasElement {
    if (this.tilesetCanvas) return this.tilesetCanvas;

    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    // Fill with black
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, 256, 256);

    // Row 0: Grass variants (tile IDs 0-3) and Grass with flowers (4-6)
    for (let i = 0; i < 4; i++) {
      this.drawGrass(ctx, i, 0, i);
    }
    for (let i = 4; i < 7; i++) {
      this.drawGrassFlowers(ctx, i, 0, i - 4);
    }

    // Row 1: Ocean/Water (tile IDs 16-21) - animated frames
    for (let i = 0; i < 6; i++) {
      this.drawOcean(ctx, i, 1, i % 4);
    }

    // Row 2: Forest (tile IDs 32-34)
    for (let i = 0; i < 4; i++) {
      this.drawForest(ctx, i, 2, i);
    }

    // Row 3: Mountain (tile IDs 48-49)
    for (let i = 0; i < 4; i++) {
      this.drawMountain(ctx, i, 3, i);
    }

    // Row 4: Desert (tile IDs 64-66)
    for (let i = 0; i < 4; i++) {
      this.drawDesert(ctx, i, 4, i);
    }

    // Row 5: Town (tile ID 80)
    this.drawTown(ctx, 0, 5);

    // Row 6: Castle (tile ID 96)
    this.drawCastle(ctx, 0, 6);

    // Row 7: Cave/Dungeon (tile ID 112) and Shrine (113)
    this.drawCave(ctx, 0, 7);
    this.drawShrine(ctx, 1, 7);

    // Row 8: Road (128-129) and Bridge (130)
    this.drawRoad(ctx, 0, 8);
    this.drawRoadHorizontal(ctx, 1, 8);
    this.drawBridge(ctx, 2, 8);

    // Row 9: Swamp (tile IDs 144-146) - animated
    for (let i = 0; i < 4; i++) {
      this.drawSwamp(ctx, i, 9, i);
    }

    // Row 10: Snow (tile IDs 160-161)
    for (let i = 0; i < 4; i++) {
      this.drawSnow(ctx, i, 10, i);
    }

    // Row 11: Lava (tile IDs 176-178) - animated
    for (let i = 0; i < 4; i++) {
      this.drawLava(ctx, i, 11, i);
    }

    this.tilesetCanvas = canvas;
    return canvas;
  }

  // Enhanced grass tile with FF1-style dithering
  private drawGrass(ctx: CanvasRenderingContext2D, col: number, row: number, variant: number): void {
    const x = col * 16;
    const y = row * 16;

    // Base grass color
    ctx.fillStyle = '#22aa22';
    ctx.fillRect(x, y, 16, 16);

    // Add texture based on variant
    ctx.fillStyle = '#118811';
    const patterns = [
      [[2, 2], [8, 4], [4, 9], [12, 11], [6, 14]],
      [[4, 3], [10, 6], [6, 12], [14, 8]],
      [[3, 5], [9, 2], [5, 10], [11, 13]],
      [[1, 4], [7, 7], [13, 3], [3, 12], [10, 10]],
    ];
    const pattern = patterns[variant % 4];
    pattern.forEach(([px, py]) => {
      ctx.fillRect(x + px, y + py, 1, 1);
    });

    // Lighter highlights
    ctx.fillStyle = '#33cc33';
    ctx.fillRect(x + 5 + variant, y + 5, 1, 1);
    ctx.fillRect(x + 11, y + 8 + (variant % 2), 1, 1);
  }

  private drawGrassFlowers(ctx: CanvasRenderingContext2D, col: number, row: number, variant: number): void {
    const x = col * 16;
    const y = row * 16;

    // Base grass
    this.drawGrass(ctx, col, row, variant);

    // Add flowers
    const flowerColors = ['#ff4444', '#ffff44', '#ffffff', '#ff88ff'];
    ctx.fillStyle = flowerColors[variant % 4];
    ctx.fillRect(x + 6 + variant, y + 6, 2, 2);
    ctx.fillRect(x + 10, y + 10 + (variant % 2), 2, 2);
  }

  private drawOcean(ctx: CanvasRenderingContext2D, col: number, row: number, frame: number): void {
    const x = col * 16;
    const y = row * 16;

    // Deep water base
    ctx.fillStyle = '#1155aa';
    ctx.fillRect(x, y, 16, 16);

    // Wave pattern based on frame
    ctx.fillStyle = '#2266dd';
    for (let py = 0; py < 16; py++) {
      for (let px = 0; px < 16; px++) {
        const pattern = (px + py + frame) % 4;
        if (pattern < 2) {
          ctx.fillRect(x + px, y + py, 1, 1);
        }
      }
    }

    // Sparkle highlights
    ctx.fillStyle = '#99ccff';
    const sparkleOffset = frame % 4;
    ctx.fillRect(x + 3 + sparkleOffset, y + 2, 1, 1);
    ctx.fillRect(x + 10 - sparkleOffset, y + 8, 1, 1);
    ctx.fillRect(x + 6, y + 13 + (frame % 2), 1, 1);
  }

  private drawTerrain(ctx: CanvasRenderingContext2D, col: number, row: number, terrain: TerrainType): void {
    const x = col * 16;
    const y = row * 16;

    // Enhanced grass with multiple shades for depth
    if (terrain === TerrainType.GRASS) {
      // Base grass color (FF1-inspired)
      ctx.fillStyle = '#22aa22';
      ctx.fillRect(x, y, 16, 16);

      // Add darker grass patches for texture (dithering pattern)
      ctx.fillStyle = '#118811';
      const variant = (col + row) % 4;
      if (variant === 0) {
        // Scattered dark pixels
        ctx.fillRect(x + 2, y + 2, 1, 1);
        ctx.fillRect(x + 8, y + 4, 1, 1);
        ctx.fillRect(x + 4, y + 9, 1, 1);
        ctx.fillRect(x + 12, y + 11, 1, 1);
      } else if (variant === 1) {
        // Different pattern
        ctx.fillRect(x + 4, y + 3, 1, 1);
        ctx.fillRect(x + 10, y + 6, 1, 1);
        ctx.fillRect(x + 6, y + 12, 1, 1);
      }

      // Add lighter highlights
      ctx.fillStyle = '#33cc33';
      ctx.fillRect(x + 5, y + 5, 1, 1);
      ctx.fillRect(x + 11, y + 8, 1, 1);
    } else if (terrain === TerrainType.GRASS_FLOWERS) {
      // Grass base
      ctx.fillStyle = '#22aa22';
      ctx.fillRect(x, y, 16, 16);

      // Texture
      ctx.fillStyle = '#118811';
      ctx.fillRect(x + 3, y + 3, 1, 1);
      ctx.fillRect(x + 9, y + 7, 1, 1);

      // Flowers - small colorful dots
      const flowerVariant = (col * 7 + row * 13) % 3;
      if (flowerVariant === 0) {
        // Red flowers
        ctx.fillStyle = '#ff4444';
        ctx.fillRect(x + 6, y + 6, 2, 2);
        ctx.fillStyle = '#ffaa44';
        ctx.fillRect(x + 6, y + 6, 1, 1);
      } else if (flowerVariant === 1) {
        // Yellow flowers
        ctx.fillStyle = '#ffff44';
        ctx.fillRect(x + 8, y + 10, 2, 2);
        ctx.fillStyle = '#ffaa00';
        ctx.fillRect(x + 8, y + 10, 1, 1);
      } else {
        // White flowers
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x + 10, y + 5, 2, 2);
        ctx.fillStyle = '#cccccc';
        ctx.fillRect(x + 10, y + 5, 1, 1);
      }
    } else if (terrain === TerrainType.DESERT) {
      // Use dedicated desert drawing method
      this.drawDesert(ctx, col, row, (col + row) % 4);
    } else {
      // Fallback for other terrain types
      const color = TERRAIN_COLORS[terrain] || '#22aa22';
      ctx.fillStyle = color;
      ctx.fillRect(x, y, 16, 16);

      // Add subtle texture
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      for (let i = 0; i < 3; i++) {
        const dx = x + 2 + (i * 4);
        const dy = y + 2 + ((i * 5) % 12);
        ctx.fillRect(dx, dy, 1, 1);
      }
    }
  }

  private drawWater(ctx: CanvasRenderingContext2D, col: number, row: number, frame: number): void {
    const x = col * 16;
    const y = row * 16;

    // FF1-style water with multiple blue shades
    // Base water (dark blue)
    ctx.fillStyle = '#1155aa';
    ctx.fillRect(x, y, 16, 16);

    // Medium blue layer (creates depth)
    ctx.fillStyle = '#2266dd';
    const animOffset = frame % 4;

    // Create animated wave pattern inspired by FF1
    // Diagonal stripe pattern that shifts with animation
    for (let py = 0; py < 16; py++) {
      for (let px = 0; px < 16; px++) {
        const pattern = (px + py + animOffset) % 4;
        if (pattern === 0 || pattern === 1) {
          ctx.fillRect(x + px, y + py, 1, 1);
        }
      }
    }

    // Light highlights (white foam/sparkle)
    ctx.fillStyle = '#99ccff';
    const sparklePattern = (col * 3 + row * 7 + animOffset) % 8;
    if (sparklePattern < 4) {
      // Scattered light pixels for sparkle effect
      ctx.fillRect(x + 3 + animOffset, y + 2, 1, 1);
      ctx.fillRect(x + 10 - animOffset, y + 8, 1, 1);
      ctx.fillRect(x + 6, y + 13 + (animOffset % 2), 1, 1);
    }
  }

  private drawForest(ctx: CanvasRenderingContext2D, col: number, row: number, variant: number): void {
    const x = col * 16;
    const y = row * 16;

    // Dark grass base for forest floor
    ctx.fillStyle = '#116611';
    ctx.fillRect(x, y, 16, 16);

    // Add darker patches for depth
    ctx.fillStyle = '#004400';
    ctx.fillRect(x + 2, y + 2, 1, 1);
    ctx.fillRect(x + 9, y + 5, 1, 1);
    ctx.fillRect(x + 4, y + 13, 1, 1);

    // Create detailed tree based on variant
    if (variant === 0 || variant === 2) {
      // Tall pine tree style
      // Dark green foliage (shadow)
      ctx.fillStyle = '#005500';
      ctx.fillRect(x + 5, y + 3, 6, 2);
      ctx.fillRect(x + 4, y + 5, 8, 3);
      ctx.fillRect(x + 5, y + 8, 6, 2);

      // Lighter green highlights
      ctx.fillStyle = '#118811';
      ctx.fillRect(x + 6, y + 3, 4, 1);
      ctx.fillRect(x + 5, y + 6, 3, 1);

      // Brown trunk
      ctx.fillStyle = '#442200';
      ctx.fillRect(x + 7, y + 10, 2, 5);

      // Trunk shadow
      ctx.fillStyle = '#221100';
      ctx.fillRect(x + 8, y + 11, 1, 4);
    } else {
      // Round deciduous tree style
      // Dark foliage base
      ctx.fillStyle = '#005500';
      ctx.fillRect(x + 4, y + 2, 8, 8);

      // Lighter green top/highlights
      ctx.fillStyle = '#118811';
      ctx.fillRect(x + 5, y + 3, 6, 3);
      ctx.fillRect(x + 6, y + 6, 4, 2);

      // Very light highlight pixels
      ctx.fillStyle = '#22aa22';
      ctx.fillRect(x + 7, y + 4, 2, 1);

      // Brown trunk
      ctx.fillStyle = '#442200';
      ctx.fillRect(x + 7, y + 10, 3, 5);

      // Trunk highlight
      ctx.fillStyle = '#664422';
      ctx.fillRect(x + 7, y + 10, 1, 4);
    }
  }

  private drawMountain(ctx: CanvasRenderingContext2D, col: number, row: number, variant: number): void {
    const x = col * 16;
    const y = row * 16;

    // Dark gray base (rock/ground)
    ctx.fillStyle = '#444444';
    ctx.fillRect(x, y, 16, 16);

    // Mountain variant determines shape
    const offsetX = variant % 2 === 0 ? 0 : 2;

    // Dark shadow side of mountain
    ctx.fillStyle = '#555555';
    ctx.fillRect(x + 1 + offsetX, y + 8, 6, 8);
    ctx.fillRect(x + 2 + offsetX, y + 5, 5, 3);
    ctx.fillRect(x + 3 + offsetX, y + 3, 4, 2);

    // Main mountain body (medium gray)
    ctx.fillStyle = '#777777';
    ctx.fillRect(x + 4 + offsetX, y + 6, 6, 10);
    ctx.fillRect(x + 5 + offsetX, y + 4, 5, 2);
    ctx.fillRect(x + 6 + offsetX, y + 3, 3, 1);

    // Lighter gray highlights (sunlit side)
    ctx.fillStyle = '#999999';
    ctx.fillRect(x + 9 + offsetX, y + 7, 3, 9);
    ctx.fillRect(x + 9 + offsetX, y + 5, 2, 2);
    ctx.fillRect(x + 8 + offsetX, y + 3, 1, 2);

    // Very light highlights (rocky texture)
    ctx.fillStyle = '#aaaaaa';
    ctx.fillRect(x + 10 + offsetX, y + 8, 1, 1);
    ctx.fillRect(x + 11 + offsetX, y + 11, 1, 1);
    ctx.fillRect(x + 9 + offsetX, y + 6, 1, 1);

    // Snow cap (white)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 6 + offsetX, y + 2, 3, 1);
    ctx.fillRect(x + 7 + offsetX, y + 3, 2, 1);
    ctx.fillRect(x + 6 + offsetX, y + 4, 1, 1);
    ctx.fillRect(x + 8 + offsetX, y + 4, 1, 1);

    // Snow shadow (light blue tint)
    ctx.fillStyle = '#ccddff';
    ctx.fillRect(x + 6 + offsetX, y + 3, 1, 1);
  }

  private drawDesert(ctx: CanvasRenderingContext2D, col: number, row: number, variant: number): void {
    const x = col * 16;
    const y = row * 16;

    // Base sand color (light tan/yellow)
    ctx.fillStyle = '#ddcc66';
    ctx.fillRect(x, y, 16, 16);

    // Darker sand for depth/shadow (creates dune effect)
    ctx.fillStyle = '#ccaa44';
    const dunePattern = variant % 4;
    if (dunePattern === 0) {
      // Horizontal dune bands
      ctx.fillRect(x, y + 6, 16, 3);
      ctx.fillRect(x, y + 12, 16, 2);
    } else if (dunePattern === 1) {
      // Diagonal dune
      for (let i = 0; i < 16; i++) {
        const h = i < 8 ? 2 : 1;
        ctx.fillRect(x + i, y + 4 + Math.floor(i / 3), 1, h);
      }
    } else if (dunePattern === 2) {
      // Scattered pattern
      ctx.fillRect(x + 2, y + 3, 5, 2);
      ctx.fillRect(x + 8, y + 7, 6, 2);
      ctx.fillRect(x + 1, y + 11, 7, 2);
    } else {
      // Wavy pattern
      ctx.fillRect(x, y + 5, 16, 2);
      ctx.fillRect(x + 3, y + 10, 10, 2);
    }

    // Light highlights (sun-bleached sand)
    ctx.fillStyle = '#eedd88';
    ctx.fillRect(x + 5 + variant, y + 2, 3, 1);
    ctx.fillRect(x + 10, y + 8 + (variant % 2), 2, 1);

    // Very sparse dark pixels (rocks/debris)
    ctx.fillStyle = '#997744';
    const rockSeed = (col * 11 + row * 7) % 5;
    if (rockSeed === 0) {
      ctx.fillRect(x + 7, y + 9, 1, 1);
      ctx.fillRect(x + 12, y + 5, 1, 1);
    }
  }

  private drawTown(ctx: CanvasRenderingContext2D, col: number, row: number): void {
    const x = col * 16;
    const y = row * 16;

    // Ground
    ctx.fillStyle = '#aa6622';
    ctx.fillRect(x, y, 16, 16);

    // Building
    ctx.fillStyle = '#884411';
    ctx.fillRect(x + 3, y + 6, 10, 9);

    // Roof
    ctx.fillStyle = '#662200';
    ctx.beginPath();
    ctx.moveTo(x + 2, y + 6);
    ctx.lineTo(x + 8, y + 2);
    ctx.lineTo(x + 14, y + 6);
    ctx.closePath();
    ctx.fill();

    // Door
    ctx.fillStyle = '#441100';
    ctx.fillRect(x + 6, y + 10, 4, 5);
  }

  private drawCastle(ctx: CanvasRenderingContext2D, col: number, row: number): void {
    const x = col * 16;
    const y = row * 16;

    // Ground
    ctx.fillStyle = '#999999';
    ctx.fillRect(x, y, 16, 16);

    // Castle walls
    ctx.fillStyle = '#cccccc';
    ctx.fillRect(x + 2, y + 4, 4, 11);
    ctx.fillRect(x + 10, y + 4, 4, 11);
    ctx.fillRect(x + 5, y + 6, 6, 9);

    // Battlements
    ctx.fillStyle = '#aaaaaa';
    ctx.fillRect(x + 2, y + 2, 2, 2);
    ctx.fillRect(x + 5, y + 2, 2, 2);
    ctx.fillRect(x + 9, y + 2, 2, 2);
    ctx.fillRect(x + 12, y + 2, 2, 2);

    // Door
    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 6, y + 10, 4, 5);
  }

  private drawDungeon(ctx: CanvasRenderingContext2D, col: number, row: number): void {
    const x = col * 16;
    const y = row * 16;

    // Dark ground
    ctx.fillStyle = '#443333';
    ctx.fillRect(x, y, 16, 16);

    // Cave entrance
    ctx.fillStyle = '#221111';
    ctx.beginPath();
    ctx.arc(x + 8, y + 16, 6, Math.PI, 0, true);
    ctx.fill();

    // Skull decoration
    ctx.fillStyle = '#888888';
    ctx.beginPath();
    ctx.arc(x + 8, y + 5, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#221111';
    ctx.fillRect(x + 6, y + 4, 1, 1);
    ctx.fillRect(x + 9, y + 4, 1, 1);
  }

  private drawCave(ctx: CanvasRenderingContext2D, col: number, row: number): void {
    const x = col * 16;
    const y = row * 16;

    // Rocky ground
    ctx.fillStyle = '#555544';
    ctx.fillRect(x, y, 16, 16);

    // Cave entrance (dark)
    ctx.fillStyle = '#221111';
    ctx.beginPath();
    ctx.arc(x + 8, y + 14, 5, Math.PI, 0, true);
    ctx.fill();

    // Rocks around entrance
    ctx.fillStyle = '#666655';
    ctx.fillRect(x + 1, y + 10, 4, 4);
    ctx.fillRect(x + 11, y + 11, 4, 3);
    ctx.fillRect(x + 5, y + 2, 6, 4);

    // Dark cracks
    ctx.fillStyle = '#333322';
    ctx.fillRect(x + 3, y + 6, 1, 3);
    ctx.fillRect(x + 12, y + 4, 1, 4);
  }

  private drawShrine(ctx: CanvasRenderingContext2D, col: number, row: number): void {
    const x = col * 16;
    const y = row * 16;

    // Mystical ground
    ctx.fillStyle = '#334466';
    ctx.fillRect(x, y, 16, 16);

    // Shrine building
    ctx.fillStyle = '#4488cc';
    ctx.fillRect(x + 4, y + 6, 8, 10);

    // Pointed roof
    ctx.fillStyle = '#66aaff';
    ctx.beginPath();
    ctx.moveTo(x + 2, y + 6);
    ctx.lineTo(x + 8, y + 1);
    ctx.lineTo(x + 14, y + 6);
    ctx.closePath();
    ctx.fill();

    // Glowing orb
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(x + 8, y + 4, 2, 0, Math.PI * 2);
    ctx.fill();

    // Door
    ctx.fillStyle = '#224466';
    ctx.fillRect(x + 6, y + 10, 4, 6);
  }

  private drawRoad(ctx: CanvasRenderingContext2D, col: number, row: number): void {
    const x = col * 16;
    const y = row * 16;

    // Dirt path base
    ctx.fillStyle = '#998866';
    ctx.fillRect(x, y, 16, 16);

    // Center path (vertical)
    ctx.fillStyle = '#aa9977';
    ctx.fillRect(x + 4, y, 8, 16);

    // Worn path markings
    ctx.fillStyle = '#887755';
    ctx.fillRect(x + 6, y + 2, 1, 4);
    ctx.fillRect(x + 9, y + 8, 1, 4);
    ctx.fillRect(x + 6, y + 12, 1, 3);
  }

  private drawRoadHorizontal(ctx: CanvasRenderingContext2D, col: number, row: number): void {
    const x = col * 16;
    const y = row * 16;

    // Dirt path base
    ctx.fillStyle = '#998866';
    ctx.fillRect(x, y, 16, 16);

    // Center path (horizontal)
    ctx.fillStyle = '#aa9977';
    ctx.fillRect(x, y + 4, 16, 8);

    // Worn path markings
    ctx.fillStyle = '#887755';
    ctx.fillRect(x + 2, y + 6, 4, 1);
    ctx.fillRect(x + 8, y + 9, 4, 1);
    ctx.fillRect(x + 12, y + 6, 3, 1);
  }

  private drawBridge(ctx: CanvasRenderingContext2D, col: number, row: number): void {
    const x = col * 16;
    const y = row * 16;

    // Water underneath
    ctx.fillStyle = '#2266ff';
    ctx.fillRect(x, y, 16, 16);

    // Bridge planks
    ctx.fillStyle = '#886644';
    ctx.fillRect(x, y + 4, 16, 8);

    // Plank lines
    ctx.strokeStyle = '#664422';
    ctx.lineWidth = 1;
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(x + 4 + i * 4, y + 4);
      ctx.lineTo(x + 4 + i * 4, y + 12);
      ctx.stroke();
    }
  }

  private drawSwamp(ctx: CanvasRenderingContext2D, col: number, row: number, frame: number): void {
    const x = col * 16;
    const y = row * 16;

    ctx.fillStyle = '#556633';
    ctx.fillRect(x, y, 16, 16);

    // Murky water patches
    ctx.fillStyle = '#445522';
    ctx.beginPath();
    ctx.arc(x + 5 + (frame % 3), y + 8, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x + 11, y + 12, 3, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawSnow(ctx: CanvasRenderingContext2D, col: number, row: number, variant: number): void {
    const x = col * 16;
    const y = row * 16;

    ctx.fillStyle = '#eeffff';
    ctx.fillRect(x, y, 16, 16);

    // Snow drifts
    ctx.fillStyle = '#ddeeff';
    ctx.beginPath();
    ctx.arc(x + 5, y + 12 + (variant % 2), 4, 0, Math.PI, true);
    ctx.fill();
  }

  private drawLava(ctx: CanvasRenderingContext2D, col: number, row: number, frame: number): void {
    const x = col * 16;
    const y = row * 16;

    // Base lava
    const r = 255;
    const g = 68 + Math.sin(frame) * 30;
    ctx.fillStyle = `rgb(${r}, ${g}, 0)`;
    ctx.fillRect(x, y, 16, 16);

    // Bright spots
    ctx.fillStyle = '#ffff00';
    ctx.beginPath();
    ctx.arc(x + 5 + frame, y + 8, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x + 11, y + 5 + frame, 2, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * Generate character sprites (192x192, 16x16 tiles, 12 classes x 12 frames)
   */
  generateCharacters(): HTMLCanvasElement {
    if (this.characterCanvas) return this.characterCanvas;

    const canvas = document.createElement('canvas');
    canvas.width = 192;
    canvas.height = 192;
    const ctx = canvas.getContext('2d')!;

    ctx.clearRect(0, 0, 192, 192);

    const classes = [
      { color: '#ff4444', armor: '#aa0000' }, // Fighter
      { color: '#44ff44', armor: '#008800' }, // Thief
      { color: '#ffaa00', armor: '#886600' }, // Monk
      { color: '#ff4488', armor: '#aa0044' }, // Red Mage
      { color: '#ffffff', armor: '#aaaaaa' }, // White Mage
      { color: '#4444ff', armor: '#000088' }, // Black Mage
      { color: '#ff8888', armor: '#cc4444' }, // Knight
      { color: '#88ff88', armor: '#44aa44' }, // Ninja
      { color: '#ffcc44', armor: '#aa8800' }, // Master
      { color: '#ff88aa', armor: '#cc4466' }, // Red Wizard
      { color: '#ffffcc', armor: '#cccc99' }, // White Wizard
      { color: '#8888ff', armor: '#4444aa' }, // Black Wizard
    ];

    for (let classIdx = 0; classIdx < 12; classIdx++) {
      const cls = classes[classIdx] ?? { color: '#ff00ff', armor: '#880088' };
      for (let frame = 0; frame < 12; frame++) {
        const direction = Math.floor(frame / 3);
        const walkFrame = frame % 3;
        this.drawCharacter(ctx, frame * 16, classIdx * 16, cls.color, cls.armor, direction, walkFrame);
      }
    }

    this.characterCanvas = canvas;
    return canvas;
  }

  private drawCharacter(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    bodyColor: string,
    armorColor: string,
    direction: number,
    walkFrame: number
  ): void {
    // Body
    ctx.fillStyle = bodyColor;
    ctx.fillRect(x + 4, y + 4, 8, 10);

    // Head (skin tone)
    ctx.fillStyle = '#ffcc99';
    ctx.fillRect(x + 5, y + 2, 6, 5);

    // Armor/Robe
    ctx.fillStyle = armorColor;
    ctx.fillRect(x + 4, y + 7, 8, 5);

    // Feet with walk animation
    ctx.fillStyle = '#442200';
    const footOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;
    ctx.fillRect(x + 4, y + 12 + footOffset, 3, 2);
    ctx.fillRect(x + 9, y + 12 - footOffset, 3, 2);

    // Eyes based on direction
    ctx.fillStyle = '#000';
    if (direction === 0) { // Down
      ctx.fillRect(x + 6, y + 4, 1, 1);
      ctx.fillRect(x + 9, y + 4, 1, 1);
    } else if (direction === 2) { // Left
      ctx.fillRect(x + 5, y + 4, 1, 1);
    } else if (direction === 3) { // Right
      ctx.fillRect(x + 10, y + 4, 1, 1);
    }
  }

  /**
   * Generate enemy sprites (256x256, 32x32 tiles)
   */
  generateEnemies(): HTMLCanvasElement {
    if (this.enemyCanvas) return this.enemyCanvas;

    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    // Clear to transparent (don't fill with black)
    ctx.clearRect(0, 0, 256, 256);

    const enemyColors = [
      '#00aa00', '#884444', '#668866', '#cccccc', '#aa6600', '#4466aa', '#442244', '#448844',
      '#886600', '#ff4400', '#886644', '#aa8866', '#668844', '#440044', '#aa9966', '#666688',
      '#8888aa', '#664488', '#aa6644', '#aa4466', '#448844', '#888844', '#666666', '#888888',
      '#446644', '#4488ff', '#ff4400', '#44aa44', '#664422', '#aaaaaa', '#222244', '#442266',
      '#666688', '#8888aa', '#aa0000', '#880000', '#6644aa', '#aa2200', '#4444aa', '#ff6600',
      '#226688', '#884422', '#880088', '#ff00ff', '#ff00ff', '#ff00ff', '#ff00ff', '#ff00ff',
      '#ff00ff', '#ff00ff', '#ff00ff', '#ff00ff', '#ff00ff', '#ff00ff', '#ff00ff', '#ff00ff',
      '#880088', '#9900aa', '#aa00bb', '#bb00cc', '#ff00ff', '#ff00ff', '#ff00ff', '#ff00ff',
    ];

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const idx = row * 8 + col;
        const color = enemyColors[idx] ?? '#ff0000';
        const x = col * 32;
        const y = row * 32;

        this.drawEnemy(ctx, x, y, color, idx);
      }
    }

    this.enemyCanvas = canvas;
    return canvas;
  }

  private drawEnemy(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, type: number): void {
    ctx.fillStyle = color;

    if (type < 10) {
      // Small enemies - blob shape
      ctx.beginPath();
      ctx.ellipse(x + 16, y + 18, 10, 12, 0, 0, Math.PI * 2);
      ctx.fill();
    } else if (type < 25) {
      // Medium enemies - humanoid shape
      ctx.fillRect(x + 8, y + 4, 16, 24);
      ctx.beginPath();
      ctx.arc(x + 16, y + 6, 6, 0, Math.PI * 2);
      ctx.fill();
    } else if (type < 40) {
      // Large enemies - dragon/beast shape
      ctx.beginPath();
      ctx.moveTo(x + 16, y + 4);
      ctx.lineTo(x + 28, y + 16);
      ctx.lineTo(x + 24, y + 28);
      ctx.lineTo(x + 8, y + 28);
      ctx.lineTo(x + 4, y + 16);
      ctx.closePath();
      ctx.fill();
    } else {
      // Bosses - large imposing shape
      ctx.fillRect(x + 4, y + 4, 24, 24);
      ctx.beginPath();
      ctx.moveTo(x + 16, y + 2);
      ctx.lineTo(x + 28, y + 8);
      ctx.lineTo(x + 4, y + 8);
      ctx.closePath();
      ctx.fill();
    }

    // Eyes
    ctx.fillStyle = '#fff';
    ctx.fillRect(x + 10, y + 12, 4, 4);
    ctx.fillRect(x + 18, y + 12, 4, 4);
    ctx.fillStyle = '#f00';
    ctx.fillRect(x + 11, y + 13, 2, 2);
    ctx.fillRect(x + 19, y + 13, 2, 2);
  }

  /**
   * Generate effect sprites (256x64, 16x16 tiles)
   */
  generateEffects(): HTMLCanvasElement {
    if (this.effectsCanvas) return this.effectsCanvas;

    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;

    // Clear to transparent (don't fill with black)
    ctx.clearRect(0, 0, 256, 64);

    // Row 0: Slash (0-2), Fire (3-6), Ice (7-10), Lightning (11-13), Heal (14-15)
    for (let i = 0; i < 3; i++) this.drawSlashEffect(ctx, i * 16, 0, i);
    for (let i = 0; i < 4; i++) this.drawFireEffect(ctx, (3 + i) * 16, 0, i);
    for (let i = 0; i < 4; i++) this.drawIceEffect(ctx, (7 + i) * 16, 0, i);
    for (let i = 0; i < 3; i++) this.drawLightningEffect(ctx, (11 + i) * 16, 0, i);
    for (let i = 0; i < 2; i++) this.drawHealEffect(ctx, (14 + i) * 16, 0, i);

    // Row 1: Heal cont (16-17), Crit (18-21), Miss (22), Poison (23-25), Death (26-29)
    for (let i = 0; i < 2; i++) this.drawHealEffect(ctx, i * 16, 16, i + 2);
    for (let i = 0; i < 4; i++) this.drawCritEffect(ctx, (2 + i) * 16, 16, i);
    this.drawMissEffect(ctx, 6 * 16, 16);
    for (let i = 0; i < 3; i++) this.drawPoisonEffect(ctx, (7 + i) * 16, 16, i);
    for (let i = 0; i < 4; i++) this.drawDeathEffect(ctx, (10 + i) * 16, 16, i);

    this.effectsCanvas = canvas;
    return canvas;
  }

  private drawSlashEffect(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number): void {
    const progress = (frame + 1) / 3;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x + 2, y + 2);
    ctx.lineTo(x + 2 + 12 * progress, y + 2 + 12 * progress);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x + 14, y + 2);
    ctx.lineTo(x + 14 - 12 * progress, y + 2 + 12 * progress);
    ctx.stroke();
  }

  private drawFireEffect(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number): void {
    const gradient = ctx.createRadialGradient(x + 8, y + 10, 0, x + 8, y + 10, 7);
    gradient.addColorStop(0, '#ffff00');
    gradient.addColorStop(0.5, `rgba(255, ${128 + frame * 30}, 0, 0.8)`);
    gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(x, y, 16, 16);
  }

  private drawIceEffect(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number): void {
    ctx.fillStyle = `rgba(150, 200, 255, ${0.5 + frame * 0.15})`;
    ctx.beginPath();
    ctx.moveTo(x + 8, y + 2);
    ctx.lineTo(x + 14, y + 8);
    ctx.lineTo(x + 8, y + 14);
    ctx.lineTo(x + 2, y + 8);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  private drawLightningEffect(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number): void {
    ctx.strokeStyle = '#ffff88';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x + 8, y);
    ctx.lineTo(x + 6 + frame, y + 5);
    ctx.lineTo(x + 10 - frame, y + 8);
    ctx.lineTo(x + 8, y + 16);
    ctx.stroke();
  }

  private drawHealEffect(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number): void {
    ctx.fillStyle = '#88ff88';
    for (let i = 0; i < 5; i++) {
      const px = x + 3 + (i * 2);
      const py = y + 14 - (i + frame) * 2;
      if (py > y && py < y + 16) {
        ctx.fillRect(px, py, 2, 2);
      }
    }
  }

  private drawCritEffect(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number): void {
    ctx.strokeStyle = '#ffff00';
    ctx.lineWidth = 2;
    const size = 4 + frame * 2;
    ctx.beginPath();
    ctx.moveTo(x + 8, y + 8 - size);
    ctx.lineTo(x + 8, y + 8 + size);
    ctx.moveTo(x + 8 - size, y + 8);
    ctx.lineTo(x + 8 + size, y + 8);
    ctx.stroke();
  }

  private drawMissEffect(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    ctx.fillStyle = '#888888';
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('MISS', x + 8, y + 10);
  }

  private drawPoisonEffect(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number): void {
    ctx.fillStyle = '#88ff00';
    for (let i = 0; i < 4; i++) {
      const px = x + 4 + (i * 3);
      const py = y + 4 + ((i + frame) * 3) % 10;
      ctx.beginPath();
      ctx.arc(px, py, 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawDeathEffect(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number): void {
    ctx.strokeStyle = '#880088';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x + 8, y + 8, 2 + frame * 3, 0, Math.PI * 2);
    ctx.stroke();
  }
}

// Singleton instance
let proceduralSpritesInstance: ProceduralSprites | null = null;

export function getProceduralSprites(): ProceduralSprites {
  if (!proceduralSpritesInstance) {
    proceduralSpritesInstance = new ProceduralSprites();
  }
  return proceduralSpritesInstance;
}
